'use client';

import { createContext, useContext } from 'react';
import { CREHANA_BRANDING } from '@crehana/constants/branding';
import { useLibraryCrehanaAuthGetCentralizedDataQuery } from "./get-centralized-data.v5.generated";
import { getSlugFromUrl } from "./utils/get-slug-from-url";
import { transformCentralizedOrgData } from "./utils/transform-centralized-org-data";
import { transformCurrentSessionSecurity } from "./utils/transform-current-session-security";
import { jsx as _jsx } from "react/jsx-runtime";
var Context = /*#__PURE__*/createContext(undefined);
export var AuthCentralizedProvider = function AuthCentralizedProvider(_ref) {
  var children = _ref.children,
    organizationSlugFromProps = _ref.organizationSlug;
  // const { me: learningMe, loading: loadingMe } = useMe();
  var organizationSlugFromUrl = getSlugFromUrl();
  // The organization Slug from props is used as a fallback when the prop is empty. Using useMe will ensure the user's organization data.
  // - it will be an empty string when we are in a url where there is not an organization slug (non b2b pages or public pages)
  var organizationSlug = organizationSlugFromProps || organizationSlugFromUrl || '';
  var _useLibraryCrehanaAut = useLibraryCrehanaAuthGetCentralizedDataQuery({
      variables: {
        organization_slug: organizationSlug || ''
      },
      context: {
        clientName: 'v5'
      }
    }),
    data = _useLibraryCrehanaAut.data,
    loading = _useLibraryCrehanaAut.loading,
    error = _useLibraryCrehanaAut.error,
    refetch = _useLibraryCrehanaAut.refetch;
  var getSessionData = function getSessionData() {
    var _data$security$curren;
    if (!data || !data.security) {
      /**
       * TODO: talk with @nando to disable/remove this later.
       * This is sending a lot of errors to Sentry making us to reach the limit of errors per month just in a couple of days
       */
      // Sentry.captureException(
      //   new Error(
      //     !data
      //       ? 'AuthCentralizedProvider: No response from the centralized auth query'
      //       : 'AuthCentralizedProvider: Security data is missing',
      //   ),
      //   {
      //     extra: {
      //       query: 'LibraryCrehanaAuthGetCentralizedData',
      //       variables: {
      //         organization_slug: organizationSlug || '',
      //       },
      //     },
      //     tags: {
      //       error_origin: 'root_error_boundary',
      //       error_type: 'critical',
      //     },
      //   },
      // );
      return undefined;
    }
    var centralizedSession = (_data$security$curren = data.security.current_session) === null || _data$security$curren === void 0 ? void 0 : _data$security$curren.centralized_session;
    var centralizedUser = centralizedSession === null || centralizedSession === void 0 ? void 0 : centralizedSession.centralized_user_organization;
    // this data will be empty usually when the org slug doesn't exists in our database
    // for protected pages,there is a global layout in the library @crehana/layouts that shows an error page when the org data is null or undefined
    var currentPageCentralizedOrganization = transformCentralizedOrgData(data.get_centralized_organization);
    if (loading || error ||
    // if the current_session is undefined, it means that the user is not logged in
    // or the session has expired
    !centralizedSession || !centralizedUser) {
      return {
        me: undefined,
        currentPageOrganization: currentPageCentralizedOrganization
      };
    }
    var currentPageOrganization =
    // eslint-disable-next-line no-nested-ternary
    currentPageCentralizedOrganization ? currentPageCentralizedOrganization : centralizedUser.organization ?
    // this fallback is usually used in pages urls that don't have an organization slug in the url
    {
      id: Number(centralizedUser.organization.id),
      name: centralizedUser.organization.name,
      slug: centralizedUser.organization.slug,
      /**
       * This data is consumed by the OrganizationBrandingProvider component from the @crehana/layouts package to apply the branding styles to the page
       */
      // use the default branding values as fallback in pages that don't have an organization branding (usually b2c or public pages)
      branding: CREHANA_BRANDING
    } : undefined;
    var security = transformCurrentSessionSecurity(data.security, data.security.talent_current_session);
    var isSuperAdminUser = security.applicationRoles.includes('ROLE_SUPERADMIN');

    /**
     * Corporations related:
     * 1. If the user is an super admin, we can check if the current organization
     *    is in the list of organizations the user can access. If not, we redirect to the home page.
     * 2. If the user is not an super admin,
     *    they can only access their own organization.
     *
     * If the can_access_organizations is undefined, we can assume that backend
     * correctly checked this is not an super admin, falling back to the second condition.
     */

    var centralizedAdminOrganizationSlugs = centralizedSession === null || centralizedSession === void 0 ? void 0 : centralizedSession.centralized_admin_organization_slugs;
    var canAccessCurrentOrganization = isSuperAdminUser && currentPageOrganization && centralizedAdminOrganizationSlugs ? centralizedAdminOrganizationSlugs.includes(currentPageOrganization.slug) : organizationSlug === centralizedUser.organization.slug;
    var sessionData = {
      me: {
        id: Number(centralizedUser.user.id),
        /**
         * @deprecated Please use our `@crehana/rbac` package instead.
         */
        helpers: {
          /**
           * @deprecated Please use our `@crehana/rbac` package instead.
           */
          isAdminOrSubAdmin: security.applicationRoles.includes('ROLE_ADMIN') || security.applicationRoles.includes('ROLE_SUPERADMIN')
        },
        organization: {
          id: Number(centralizedUser.organization.id),
          slug: centralizedUser.organization.slug,
          name: centralizedUser.organization.name
        },
        canAccessOrganizations: centralizedAdminOrganizationSlugs || undefined,
        canAccessCurrentOrganization: canAccessCurrentOrganization,
        user: {
          id: centralizedUser.user.id,
          email: centralizedUser.user.email,
          firstname: centralizedUser.user.first_name,
          fullName: centralizedUser.user.full_name,
          lastname: centralizedUser.user.last_name,
          avatarUrl: centralizedUser.avatar_url || undefined
        },
        status: centralizedUser.status,
        area: centralizedUser.area ? {
          id: centralizedUser.area.id,
          label: centralizedUser.area.label,
          name: centralizedUser.area.name,
          createdAt: centralizedUser.area.created_at
        } : undefined,
        headquarter: centralizedUser.headquarter ? {
          id: centralizedUser.headquarter.id,
          name: centralizedUser.headquarter.name
        } : undefined,
        position: centralizedUser.position ? {
          id: centralizedUser.position.id,
          name: centralizedUser.position.name
        } : undefined,
        positionCategory: centralizedUser.position_category ? {
          id: centralizedUser.position_category.id,
          name: centralizedUser.position_category.name
        } : undefined,
        security: security
      },
      currentPageOrganization: currentPageOrganization
    };
    if (security.applicationRoles.includes('ROLE_STAFF') && currentPageOrganization) {
      // If a staff user navigates to pages of another organization,
      // we need to use the organization data based on the current page's slug
      // to simulate the session data of the current page organization
      sessionData.me.organization = currentPageOrganization;
      // We must also allow the staff user to bypass corporation access
      // But we dont override the canAccessOrganizations we dont want to convert
      // every organization into a centralized corporation.
      // This is no issue as we already allow the staff user to access all
      // organizations as fake user of them.
      sessionData.me.canAccessCurrentOrganization = true;
    }
    return sessionData;
  };
  var sessionData = getSessionData();
  return /*#__PURE__*/_jsx(Context.Provider, {
    value: {
      me: sessionData === null || sessionData === void 0 ? void 0 : sessionData.me,
      currentPageOrganization: sessionData === null || sessionData === void 0 ? void 0 : sessionData.currentPageOrganization,
      loading: loading,
      error: error,
      refetch: refetch
    },
    children: children
  });
};
export var useCentralizedProviderData = function useCentralizedProviderData() {
  var context = useContext(Context);
  if (!context) {
    throw new Error('useCentralizedMe must be used within a AuthCentralizedProvider');
  }
  return context;
};
export var useCentralizedMe = function useCentralizedMe() {
  var _useCentralizedProvid = useCentralizedProviderData(),
    me = _useCentralizedProvid.me,
    currentPageOrganization = _useCentralizedProvid.currentPageOrganization,
    loading = _useCentralizedProvid.loading,
    error = _useCentralizedProvid.error;
  if (!me) {
    throw new Error('The user must be logged in to use useCentralizedMe. Make sure you are waiting for the query to finish loading before calling this hook.');
  }
  if (!currentPageOrganization) {
    throw new Error("The current organization slug, from the url, doesn't exists. Make sure the url is correct.");
  }
  return {
    me: me,
    currentPageOrganization: currentPageOrganization,
    loading: loading,
    error: error
  };
};